import { type CSSProperties, useMemo } from "react";

import type {
  AppImageProps,
  AppLinkAdapter,
  AppLinkProps,
  AppRouter,
  AppSearchParams,
} from "@/ui/app-link/types";

const FILL_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
};

export function PlainLink(props: AppLinkProps) {
  const { prefetch, replace, scroll, ...anchor } = props;
  return <a {...anchor} />;
}

export function PlainImage(props: AppImageProps) {
  const { src, fill, priority, unoptimized, style, ...img } = props;
  const asset = typeof src === "string" ? null : src;
  return (
    <img
      {...img}
      src={typeof src === "string" ? src : src.src}
      width={fill ? undefined : (img.width ?? asset?.width)}
      height={fill ? undefined : (img.height ?? asset?.height)}
      loading={priority ? "eager" : (img.loading ?? "lazy")}
      style={fill ? { ...FILL_STYLE, ...style } : style}
    />
  );
}

/** Full page loads: the browser is the only router. */
const documentRouter: AppRouter = {
  push: (href) => window.location.assign(href),
  replace: (href) => window.location.replace(href),
  back: () => window.history.back(),
  refresh: () => window.location.reload(),
  prefetch: () => undefined,
};

/** The document's location; the root while rendering on a server. */
function documentLocation(): { pathname: string; search: string } {
  return typeof window === "undefined"
    ? { pathname: "/", search: "" }
    : window.location;
}

function usePlainSearchParams(): AppSearchParams {
  const search = documentLocation().search;
  return useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * Plain `<a>`, `<img>` and document navigation: what the seam renders
 * without a provider. The location reads once per render; nothing
 * re-renders when it changes.
 */
export const plainAdapter: AppLinkAdapter = {
  Link: PlainLink,
  Image: PlainImage,
  useRouter: () => documentRouter,
  usePathname: () => documentLocation().pathname,
  useSearchParams: usePlainSearchParams,
  useLinkPending: () => false,
};
